import React, { useState, useEffect, useMemo } from 'react';
import { NewDashboardHeader } from '../../components/NewDashboardHeader';
import { Footer } from '../../components/Footer';
import { 
    IdentificationIcon, CalendarDaysIcon, 
    DocumentTextIcon, SpinnerIcon, MapPinIcon, ExclamationTriangleIcon,
    PlusIcon, CashIcon, KeyIcon
} from '../../components/Icons';
import { searchByCodesApi } from '../../services/propertyApiClient';
import { fetchInmobiliariasConfigApi } from '../../services/configApiClient';
import { Property } from '../../types';
import { ProcessStep } from '../../components/broker/sales/ProcessStep';
import { SalesProcessSidebar } from '../../components/broker/sales/SalesProcessSidebar';
import { SALES_PROCESS_FALLBACK, SALES_PROCESS_STEPS } from '../../config/salesProcessDefaults';

const STEP_ICONS: Record<string, React.ReactNode> = {
    SOLICITUD_VISITA: <IdentificationIcon className="w-6 h-6" />,
    GESTION_VISITA: <CalendarDaysIcon className="w-6 h-6" />,
    VISITA_CONFIRMADA: <MapPinIcon className="w-6 h-6" />,
    PRESENTACION_OFERTA: <PlusIcon className="w-6 h-6" />,
    EVALUACION_OFERTA: <DocumentTextIcon className="w-6 h-6" />,
    SEPARACION: <CashIcon className="w-6 h-6" />,
    PROMESA_COMPRAVENTA: <DocumentTextIcon className="w-6 h-6" />,
    GESTION_ESCRITURACION: <IdentificationIcon className="w-6 h-6" />,
    ENTREGA: <KeyIcon className="w-6 h-6" />
};

export const SalesProcessView: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [property, setProperty] = useState<Property | null>(null);
    const [funnelConfig, setFunnelConfig] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);

    const searchParams = useMemo(() => {
        const hash = window.location.hash;
        if (!hash.includes('?')) return new URLSearchParams();
        return new URLSearchParams(hash.split('?')[1]);
    }, []);

    const propertyCode = searchParams.get('code');
    const roleGroup = (searchParams.get('roleGroup') || 'BROKER').toUpperCase();

    useEffect(() => {
        const fetchData = async () => {
            if (!propertyCode) { setError("No se especificó un código."); setLoading(false); return; }
            try {
                const searchRes = await searchByCodesApi([propertyCode]);
                const foundProp = searchRes.items?.[0];
                if (!foundProp) throw new Error("Inmueble no encontrado.");
                setProperty(foundProp);
                
                const prefix = propertyCode.split('-')[0];
                const inmoConfig = await fetchInmobiliariasConfigApi();
                const docData = inmoConfig[prefix];
                if (docData) {
                    const normalize = (s: string) => s.toUpperCase().replace(/[\s-]+/g, '_').normalize("NFD").replace(/[\u0300-\u036f]/g, "");
                    const ownerKey = Object.keys(docData).find(k => normalize(k) === normalize(foundProp.owner || ""));
                    if (ownerKey) setFunnelConfig(docData[ownerKey]);
                }
            } catch (e: any) { setError(e.message || "Error al cargar datos."); } finally { setLoading(false); }
        };
        fetchData();
    }, [propertyCode]);


    const getStepDescription = (key: string) => {
        const rawValue = funnelConfig?.[key];
        if (!rawValue) return SALES_PROCESS_FALLBACK;
        if (typeof rawValue === 'string') return rawValue;
        if (typeof rawValue === 'object' && rawValue !== null) return rawValue[roleGroup] || Object.values(rawValue).find(v => typeof v === 'string') || SALES_PROCESS_FALLBACK;
        return SALES_PROCESS_FALLBACK;
    };

    if (loading) return <div className="flex flex-col items-center justify-center py-32 space-y-4"><SpinnerIcon className="w-12 h-12 text-sky-600" /><p className="text-gray-500 font-black uppercase text-xs">Cargando...</p></div>;
    if (error) return <div className="max-w-xl mx-auto py-20 px-6 text-center"><ExclamationTriangleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" /><p className="text-gray-600 mb-8">{error}</p></div>;

    return (
        <div className="min-h-screen bg-white flex flex-col font-sans">
            <NewDashboardHeader isNewlyAddedMode={false} />
            <main className="flex-grow">
                <section className="bg-gray-50 border-b border-gray-100 py-16 px-6">
                    <div className="max-w-4xl mx-auto">
                        <div className="flex items-center gap-2 mb-8">
                            <div className="px-3 py-1 bg-sky-100 text-sky-700 text-[10px] font-black uppercase rounded-lg tracking-wider border border-sky-200">Grupo: {roleGroup}</div>
                            <div className="px-3 py-1 bg-white text-gray-500 text-[10px] font-black uppercase rounded-lg tracking-wider border border-gray-200">Cód: {propertyCode}</div>
                        </div>
                        <h1 className="text-4xl md:text-5xl font-black text-gray-900 uppercase tracking-tighter leading-none">Ruta de éxito <span className="text-sky-600">de Venta</span></h1>
                    </div>
                </section>
                <section className="py-20 px-6">
                    <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12">
                        <div className="md:col-span-8">
                            {SALES_PROCESS_STEPS.map((step, idx) => (
                                <ProcessStep key={step.key} number={step.number} title={step.title} description={getStepDescription(step.key)} icon={STEP_ICONS[step.key]} isLast={idx === SALES_PROCESS_STEPS.length - 1} />
                            ))}
                        </div>
                        <div className="md:col-span-4">
                            <SalesProcessSidebar 
                                owner={property?.owner} 
                                roleGroup={roleGroup} 
                                onClose={() => window.close()} 
                            />
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
};